import React from 'react';
import Pie from '@splunk/dashboard-visualizations/Pie';
import VisualizationDocPage from '@splunk/visualizations-shared/VisualizationDocPage';
import EncodingDoc from '@splunk/dashboard-visualizations/common/EncodingDoc';
import OptionsDoc from '@splunk/dashboard-visualizations/common/OptionsDoc';
import EventsDoc from '@splunk/dashboard-visualizations/common/EventsDoc';
import DocExample from '@splunk/react-docs/DocExample';

import BasicExampleCode from '!!raw-loader!./examples/Basic';
import BackgroundColorCode from '!!raw-loader!./examples/BackgroundColor';
import ForegroundColorCode from '!!raw-loader!./examples/ForegroundColor';
import FontColorCode from '!!raw-loader!./examples/FontColor';
import SeriesColorsCode from '!!raw-loader!./examples/SeriesColors';
import FieldColorsCode from '!!raw-loader!./examples/FieldColors';
import NoLabelsExampleCode from '!!raw-loader!./examples/NoLabels';
import LabelWithPercentExampleCode from '!!raw-loader!./examples/LabelWithPercent';
import ThresholdExampleCode from '!!raw-loader!./examples/Threshold';
import CollapsingLabelExampleCode from '!!raw-loader!./examples/CollapsingLabel';
import TruncatedSizeExampleCode from '!!raw-loader!./examples/TruncatedSize';
import DonutExampleCode from '!!raw-loader!./examples/Donut';
import DonutTopLegendExampleCode from '!!raw-loader!./examples/DonutTopLegend';
import DonutOverflowEndExampleCode from '!!raw-loader!./examples/DonutOverflowEnd';

import BasicExample from './examples/Basic';
import BackgroundColorExample from './examples/BackgroundColor';
import ForegroundColorExample from './examples/ForegroundColor';
import FontColorExample from './examples/FontColor';
import SeriesColorsExample from './examples/SeriesColors';
import FieldColorsExample from './examples/FieldColors';
import NoLabelsExample from './examples/NoLabels';
import LabelWithPercentExample from './examples/LabelWithPercent';
import ThresholdExample from './examples/Threshold';
import CollapsingLabelExample from './examples/CollapsingLabel';
import TruncatedSizeExample from './examples/TruncatedSize';
import DonutExample from './examples/Donut';
import DonutTopLegendExample from './examples/DonutTopLegend';
import DonutOverflowEndExample from './examples/DonutOverflowEnd';

import doc from './definition';
import optionsSchema from '../optionsSchema';

function PieDoc() {
    return (
        <VisualizationDocPage
            title="Pie"
            examplesSection={[
                <DocExample
                    title="Basic Pie"
                    key="basic_pie"
                    description="Pie takes 100% width and 250px height by default."
                    code={BasicExampleCode}
                    example={BasicExample}
                />,
                <DocExample
                    title="Background Color - Orange"
                    key="backgroundcolor"
                    description="Background color specified."
                    code={BackgroundColorCode}
                    example={BackgroundColorExample}
                />,
                <DocExample
                    title="Foreground Color - Red"
                    key="foregroundcolor"
                    description="Foreground color specified."
                    code={ForegroundColorCode}
                    example={ForegroundColorExample}
                />,
                <DocExample
                    title="Font Color - Purple"
                    key="fontcolor"
                    description="Font color specified."
                    code={FontColorCode}
                    example={FontColorExample}
                />,
                <DocExample
                    title="Series Colors: Yellow, Blue, Green"
                    key="seriescolors"
                    description="Series colors specified."
                    code={SeriesColorsCode}
                    example={SeriesColorsExample}
                />,
                <DocExample
                    title="Field Color: December - Green, May - Red"
                    key="fieldcolors"
                    description="Field colors specified."
                    code={FieldColorsCode}
                    example={FieldColorsExample}
                />,
                <DocExample
                    title="No Labels Pie"
                    key="no_labels_pie"
                    description="No Labels will be rendered when chart.showLabels equals to false. Defaults to true."
                    code={NoLabelsExampleCode}
                    example={NoLabelsExample}
                />,
                <DocExample
                    title="Pie Labels with Percent"
                    key="labels_with_percent_pie"
                    description="Labels with Percent will be rendered when chart.showPercent equals to true. Defaults to false."
                    code={LabelWithPercentExampleCode}
                    example={LabelWithPercentExample}
                />,
                <DocExample
                    title="Slice Collapsing Threshold - 5"
                    key="slice_collapsing_threshold"
                    description="The threshold at which smaller slices collapse into a consolidated slice. This option only applies when there are more than 10 slices. 0 indicates no collapsing. Valid values are between 0 and 1. Number 1 indicates all slices collapse into a single pie. The default value, 0.01, collapses slices smaller than 1% of the whole pie."
                    code={ThresholdExampleCode}
                    example={ThresholdExample}
                />,
                <DocExample
                    title="Custom Collapsing Label - New Collapsing Label"
                    key="collapsing_label"
                    description="The label for the consolidated slice. Defaults to 'other'."
                    code={CollapsingLabelExampleCode}
                    example={CollapsingLabelExample}
                />,
                <DocExample
                    title="Truncated Size Pie"
                    key="truncated_size_pie"
                    description="A truncated size pie will be rendered when providing width and height."
                    code={TruncatedSizeExampleCode}
                    example={TruncatedSizeExample}
                />,
                <DocExample
                    title="Donut"
                    key="donut"
                    description="A donut-like pie."
                    code={DonutExampleCode}
                    example={DonutExample}
                />,
                <DocExample
                    title="Donut with Legend on Top"
                    key="donut_top_legend"
                    description="A donut-like pie with legend on top."
                    code={DonutTopLegendExampleCode}
                    example={DonutTopLegendExample}
                />,
                <DocExample
                    title="Donut Legend Label Overflow Mode - End"
                    key="donut_overflow_end"
                    description="A donut-like pie with legend label overflow mode is end."
                    code={DonutOverflowEndExampleCode}
                    example={DonutOverflowEndExample}
                />,
            ]}
            encodingSection={<EncodingDoc encoding={doc.encoding} />}
            optionSection={<OptionsDoc options={optionsSchema} />}
            eventSection={
                <EventsDoc
                    events={doc.events}
                    Visualization={Pie}
                    encoding={{
                        value: 'primary.count',
                        label: 'primary.component',
                    }}
                    dsState={{
                        primary: {
                            requestParams: { offset: 0, count: 20 },
                            data: {
                                fields: [{ name: 'component' }, { name: 'count' }],
                                columns: [
                                    [
                                        'January',
                                        'February',
                                        'March',
                                        'April',
                                        'May',
                                        'June',
                                        'July',
                                        'August',
                                        'September',
                                        'October',
                                        'November',
                                        'December',
                                    ],
                                    ['1', '1', '1', '2', '10', '10', '5', '5', '5', '5', '5', '50'],
                                ],
                            },
                            meta: { totalCount: 20 },
                        },
                    }}
                />
            }
        />
    );
}

export default PieDoc;
